import { ProfileTier, Profile } from '@fcom/core-api/login';
import { LocalDate } from '@fcom/utils';

export const TIER_LIMITS = {
  [ProfileTier.SILVER]: 30000,
  [ProfileTier.GOLD]: 80000,
  [ProfileTier.PLATINUM]: 150000,
  [ProfileTier.LUMO]: 450000,
};

export const TIER_FLIGHT_LIMITS = {
  [ProfileTier.SILVER]: 20,
  [ProfileTier.GOLD]: 46,
  [ProfileTier.PLATINUM]: 76,
  [ProfileTier.LUMO]: 150,
};

export const LIFETIME_TIER_TEXT = 'Lifetime';

export const LIFETIME_TIER_LIMITS = {
  [ProfileTier.GOLD]: 3000000,
  [ProfileTier.PLATINUM]: 5000000,
};

export enum TierState {
  MAINTAIN = 'maintain',
  GAIN = 'gain',
  HIGHEST = 'highest',
  HOLD = 'hold',
}

export enum TierName {
  BASIC = 'Basic',
  SILVER = 'Silver',
  GOLD = 'Gold',
  PLATINUM = 'Platinum',
  LUMO = 'Platinum Lumo',
  JUNIOR = 'Junior',
}

export const tierExpiryDate = (memberProfile: Profile): LocalDate =>
  new LocalDate(memberProfile.currentPeriod.trackingPeriodEnd).plusYears(
    getTierState(memberProfile) === TierState.GAIN ? 1 : 0
  );

export const getTierState = (memberProfile: Profile): TierState => {
  if (memberProfile.statusHold) {
    return TierState.HOLD;
  }
  // Basic tier user can only gain points to Silver
  if (memberProfile.tier === ProfileTier.BASIC) {
    return TierState.GAIN;
    // If the user has gained less points in the current period than is needed for the current tier, show "maintain tier" state
  } else if (
    TIER_LIMITS[memberProfile.tier] > memberProfile.currentPeriod.tierPointsCollected &&
    TIER_FLIGHT_LIMITS[memberProfile.tier] > memberProfile.currentPeriod.qualifyingFlightsFlown &&
    !lifetimeTier(memberProfile)
  ) {
    return TierState.MAINTAIN;
    // If the user has gained more points than needed for the current tier, but the current tier is lumo, show "you have reached highest tier" text
  } else if (memberProfile.tier === ProfileTier.LUMO) {
    if (
      memberProfile.currentPeriod.aySecretNumQFlightCalc < memberProfile.currentPeriod.aySecretNumQFlightRequired &&
      memberProfile.currentPeriod.aySecretTierPoints < memberProfile.currentPeriod.aySecretTierPointsRequired
    ) {
      return TierState.MAINTAIN;
    }
    return TierState.HIGHEST;
    // If the user has gained more points than needed for the current tier, which is not Basic or Lumo, show "gain next tier" state
  } else {
    return TierState.GAIN;
  }
};

export const lifetimeTier = (memberProfile: Profile): ProfileTier => {
  if (memberProfile.lifetimeTierPoints >= LIFETIME_TIER_LIMITS[ProfileTier.PLATINUM]) {
    return ProfileTier.PLATINUM;
  } else if (memberProfile.lifetimeTierPoints >= LIFETIME_TIER_LIMITS[ProfileTier.GOLD]) {
    return ProfileTier.GOLD;
  }
  return undefined;
};

export const getCurrentTierText = (memberProfile: Profile): string => {
  const currentLifetimeTier: ProfileTier | undefined = memberProfile.isJuniorMember
    ? undefined
    : lifetimeTier(memberProfile);

  const isCurrentTierTheLifetimeTier =
    (currentLifetimeTier === ProfileTier.PLATINUM && memberProfile.tier !== ProfileTier.LUMO) ||
    (currentLifetimeTier === ProfileTier.GOLD &&
      memberProfile.tier !== ProfileTier.LUMO &&
      memberProfile.tier !== ProfileTier.PLATINUM);

  const currentTier = isCurrentTierTheLifetimeTier
    ? `${LIFETIME_TIER_TEXT} ${currentLifetimeTier}`
    : TIER_DATA_MAP[memberProfile.tier].tierName;

  if (memberProfile.isJuniorMember && memberProfile.tier !== ProfileTier.JUNIOR) {
    return `${TierName.JUNIOR} ${currentTier}`;
  }

  return currentTier;
};

export const TIER_DATA_MAP = {
  [ProfileTier.BASIC]: {
    membership: 'plus.dashboard.membership.basic',
    cardIcon: 'plus-card-basic-bg',
    tierName: TierName.BASIC,
    tierKey: 'basic',
  },
  [ProfileTier.SILVER]: {
    membership: 'plus.dashboard.membership.silver',
    cardIcon: 'plus-card-silver-bg',
    tierName: TierName.SILVER,
    tierKey: 'silver',
  },
  [ProfileTier.GOLD]: {
    membership: 'plus.dashboard.membership.gold',
    cardIcon: 'plus-card-gold-bg',
    tierName: TierName.GOLD,
    tierKey: 'gold',
  },
  [ProfileTier.PLATINUM]: {
    membership: 'plus.dashboard.membership.platinum',
    cardIcon: 'plus-card-platinum-bg',
    tierName: TierName.PLATINUM,
    tierKey: 'platinum',
  },
  [ProfileTier.LUMO]: {
    membership: 'plus.dashboard.membership.lumo',
    cardIcon: 'plus-card-lumo-bg',
    tierName: TierName.LUMO,
    tierKey: 'lumo',
  },
  [ProfileTier.JUNIOR]: {
    membership: 'plus.dashboard.membership.junior',
    cardIcon: 'plus-card-junior-bg',
    tierName: TierName.JUNIOR,
    tierKey: 'junior',
  },
};
